import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BadgeIndianRupee,
  Building2,
  CalendarClock,
  ClipboardList,
  HandCoins,
  RefreshCw,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loading, PageHeader, inr } from "../../components/shared";

const PERIOD_OPTIONS = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["this_week", "This Week"],
  ["this_month", "This Month"],
  ["last_month", "Last Month"],
  ["this_quarter", "This Quarter"],
  ["this_financial_year", "This Financial Year"],
  ["last_financial_year", "Last Financial Year"],
  ["custom", "Custom Range"],
];

const COLORS = ["#1d4ed8", "#0f766e", "#d97706", "#be123c", "#7c3aed", "#475569"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("this_month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    if (period === "custom" && (!startDate || !endDate)) return;
    try {
      const response = await api.get("/admin/management/dashboard", {
        params: { period, start_date: startDate, end_date: endDate },
      });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load Management Dashboard");
      setData({ permissions: {} });
    }
  };

  useEffect(() => {
    load();
  }, [period, startDate, endDate]);

  const financial = data?.financial;
  const collectionPercent = useMemo(
    () => financial?.collection_percent ?? 0,
    [financial],
  );

  if (!data) return <Loading />;

  return (
    <div data-testid="management-dashboard-page">
      <PageHeader
        title="Management Command Centre"
        subtitle="Authoritative business, commercial, CRM, and operations signals."
      >
        <Button data-testid="management-refresh-button" onClick={load} variant="outline">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </PageHeader>

      <section className="mb-8 flex flex-wrap items-end gap-3" data-testid="management-period-controls">
        <label className="grid gap-1 text-sm font-medium" htmlFor="management-period-select">
          Reporting period
          <select
            data-testid="management-period-select"
            id="management-period-select"
            onChange={(event) => setPeriod(event.target.value)}
            value={period}
          >
            {PERIOD_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        {period === "custom" && (
          <>
            <label className="grid gap-1 text-sm font-medium" htmlFor="management-start-date">
              Start date
              <input
                data-testid="management-start-date"
                id="management-start-date"
                onChange={(event) => setStartDate(event.target.value)}
                type="date"
                value={startDate}
              />
            </label>
            <label className="grid gap-1 text-sm font-medium" htmlFor="management-end-date">
              End date
              <input
                data-testid="management-end-date"
                id="management-end-date"
                onChange={(event) => setEndDate(event.target.value)}
                type="date"
                value={endDate}
              />
            </label>
          </>
        )}
        <p className="pb-1 text-xs text-muted-foreground">
          {data.period?.start_date} to {data.period?.end_date} · FY starts {data.period?.financial_year_start}
        </p>
      </section>

      {data.permissions?.financial && financial && (
        <DashboardBand title="Executive financial KPIs" subtitle="Billed and collected use the selected period. Outstanding and overdue are current-state balances.">
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <KpiCard icon={BadgeIndianRupee} label="Billed" onClick={() => navigate("/admin/commercial?filter=billed")} testId="management-billed-kpi" tone="blue" value={inr(financial.period?.billed)} />
            <KpiCard icon={HandCoins} label="Collected" onClick={() => navigate("/admin/commercial?filter=paid")} testId="management-collected-kpi" tone="green" value={inr(financial.period?.collected)} />
            <KpiCard icon={AlertTriangle} label="Outstanding" onClick={() => navigate("/admin/commercial?filter=outstanding")} testId="management-outstanding-kpi" tone="amber" value={inr(financial.current_state?.outstanding)} />
            <KpiCard icon={AlertTriangle} label="Overdue" onClick={() => navigate("/admin/commercial?filter=overdue")} testId="management-overdue-kpi" tone="rose" value={inr(financial.current_state?.overdue)} />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <Fact label="Expenses" value={inr(financial.period?.expenses)} />
            <Fact label="Net cash movement" value={inr(financial.net_cash_movement)} />
            <Fact label="Collection performance" value={`${collectionPercent.toFixed(2)}%`} />
          </div>
          <p className="mt-4 text-xs text-muted-foreground" data-testid="management-aging-note">
            {financial.aging_note}
          </p>
        </DashboardBand>
      )}

      {data.permissions?.commercial && data.commercial && (
        <DashboardBand title="Commercial health" subtitle="Current lifecycle states reused directly from Commercial Control.">
          <div className="grid gap-4 md:grid-cols-3">
            <HealthBlock icon={Building2} label="Society subscriptions" metrics={data.commercial.society} onClick={() => navigate("/admin/commercial?tab=renewals&type=society")} testId="management-society-health" />
            <HealthBlock icon={Users} label="Vendor subscriptions" metrics={data.commercial.vendor} onClick={() => navigate("/admin/commercial?tab=renewals&type=vendor")} testId="management-vendor-health" />
            <HealthBlock icon={CalendarClock} label="AMC renewals" metrics={data.commercial.amc} onClick={() => navigate("/admin/commercial?tab=renewals&type=amc")} testId="management-amc-health" />
          </div>
        </DashboardBand>
      )}

      {data.permissions?.financial && financial && (
        <section className="grid gap-6 xl:grid-cols-2">
          <ChartPanel title="Billed vs collected" testId="management-billing-trend">
            <ResponsiveContainer height={280} width="100%">
              <BarChart data={financial.trend || []} margin={{ left: 8, right: 12 }}>
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => inr(value)} />
                <Legend />
                <Bar dataKey="billed" fill="#1d4ed8" name="Billed" />
                <Bar dataKey="collected" fill="#0f766e" name="Collected" />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
          <ChartPanel title="Revenue mix" testId="management-revenue-mix">
            <ResponsiveContainer height={280} width="100%">
              <PieChart>
                <Pie data={financial.revenue_mix || []} dataKey="amount" nameKey="source" outerRadius={95}>
                  {(financial.revenue_mix || []).map((item, index) => <Cell fill={COLORS[index % COLORS.length]} key={item.source} />)}
                </Pie>
                <Tooltip formatter={(value) => inr(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </section>
      )}

      {data.permissions?.financial && financial && (
        <DashboardBand title="Top outstanding parties" subtitle="Current Company Finance receivables by party; no separate party ledger is created here.">
          <CompactTable
            headers={["Party", "Type", "Outstanding", "Oldest due", "Overdue"]}
            rows={(financial.top_outstanding || []).map((item) => [
              item.party,
              item.party_type,
              inr(item.outstanding),
              item.oldest_due_date || "—",
              inr(item.overdue_amount),
            ])}
            testId="management-top-outstanding"
          />
        </DashboardBand>
      )}

      {data.permissions?.crm && data.crm && (
        <DashboardBand title="CRM management summary" subtitle="Current pipeline and follow-up signals from the existing CRM.">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard icon={Users} label="Active leads" onClick={() => navigate("/admin/leads")} testId="management-active-leads-kpi" tone="blue" value={data.crm.active_leads} />
            <KpiCard icon={Users} label="New leads" onClick={() => navigate("/admin/leads?status=NEW")} testId="management-new-leads-kpi" tone="blue" value={data.crm.new} />
            <KpiCard icon={CalendarClock} label="Follow-ups due" onClick={() => navigate("/admin/leads?followup=today")} testId="management-followups-due-kpi" tone="amber" value={data.crm.followups_due_today} />
            <KpiCard icon={AlertTriangle} label="Follow-ups overdue" onClick={() => navigate("/admin/leads?followup=overdue")} testId="management-followups-overdue-kpi" tone="rose" value={data.crm.overdue_followups} />
            <KpiCard icon={Users} label="Converted" onClick={() => navigate("/admin/leads?conversion=converted")} testId="management-converted-kpi" tone="green" value={data.crm.converted} />
            <KpiCard icon={AlertTriangle} label="Lost / closed" onClick={() => navigate("/admin/leads?conversion=lost")} testId="management-lost-leads-kpi" tone="rose" value={data.crm.lost} />
            <KpiCard icon={CalendarClock} label="Meetings scheduled" onClick={() => navigate("/admin/leads?meeting=scheduled")} testId="management-meetings-kpi" tone="amber" value={data.crm.meetings_scheduled} />
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <CompactTable headers={["Pipeline stage", "Leads"]} rows={(data.crm.pipeline || []).map((item) => [item.stage, item.count])} testId="management-crm-pipeline" />
            <CompactTable headers={["Lead source", "Leads", "Conversions"]} rows={(data.crm.source_performance || []).map((item) => [item.source, item.leads, item.conversions])} testId="management-lead-sources" />
          </div>
        </DashboardBand>
      )}

      {data.permissions?.operations && (
        <section className="grid gap-6 xl:grid-cols-2">
          <DashboardBand title="Applications requiring action" subtitle="Existing application status workflow, summarized only.">
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(data.applications || {}).map(([kind, metrics]) => <Fact key={kind} label={`${kind} pending`} value={metrics.pending ?? 0} />)}
            </div>
            <Button className="mt-5" data-testid="management-applications-link" onClick={() => navigate("/admin/applications?pending=true")} variant="outline">Review applications</Button>
          </DashboardBand>
          <DashboardBand title="Requirements & projects" subtitle="Operational totals reuse requirements, RFQ, quote, and project records.">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Fact label="New requirements" value={data.operations?.requirements?.new ?? 0} />
              <Fact label="Open requirements" value={data.operations?.requirements?.open ?? 0} />
              <Fact label="RFQs published" value={data.operations?.rfqs_published ?? 0} />
              <Fact label="Quotes received" value={data.operations?.quotes_received ?? 0} />
              <Fact label="Active projects" value={data.operations?.projects?.active ?? 0} />
              <Fact label="Completed projects" value={data.operations?.projects?.completed ?? 0} />
            </div>
            <Button className="mt-5" data-testid="management-requirements-link" onClick={() => navigate("/admin/requirements")} variant="outline">View requirements</Button>
          </DashboardBand>
        </section>
      )}

      {data.permissions?.operations && data.operations && (
        <DashboardBand title="Service Master business insights" subtitle="Demand uses existing Service Master links and fulfillment snapshots only.">
          <div className="grid gap-5 lg:grid-cols-2">
            <CompactTable headers={["Most requested service", "Requirements"]} rows={(data.operations.service_demand || []).map((item) => [item.service, item.count])} testId="management-service-demand" />
            <CompactTable headers={["Fulfillment model", "Requirements"]} rows={(data.operations.fulfilment_demand || []).map((item) => [item.model, item.count])} testId="management-fulfilment-demand" />
          </div>
        </DashboardBand>
      )}

      {data.permissions?.operations && data.network && (
        <DashboardBand title="Network & ecosystem" subtitle="Live platform size using existing status definitions.">
          <div className="grid gap-4 md:grid-cols-4">
            <Fact label="Societies / active" value={`${data.network.societies.total} / ${data.network.societies.active}`} />
            <Fact label="Vendors / verified" value={`${data.network.vendors.total} / ${data.network.vendors.verified}`} />
            <Fact label="Experts / verified" value={`${data.network.experts.total} / ${data.network.experts.verified}`} />
            <Fact label="Residents / members" value={data.network.members} />
          </div>
        </DashboardBand>
      )}

      <DashboardBand title="Needs attention" subtitle="Read-only priorities generated from current authoritative records.">
        {(data.attention || []).length === 0 ? <p className="text-sm text-muted-foreground">No attention signals are currently available to your access level.</p> : <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{data.attention.map((item) => <button className="border-l-4 border-amber-500 bg-card p-4 text-left transition-colors hover:bg-muted" data-testid={`management-attention-${item.key}`} key={item.key} onClick={() => navigate(item.route)}><div className="text-sm text-muted-foreground">{item.label}</div><div className="mt-1 font-display text-2xl font-bold">{typeof item.value === "number" && item.key === "overdue" ? inr(item.value) : item.value}</div></button>)}</div>}
      </DashboardBand>
    </div>
  );
}

function DashboardBand({ children, subtitle, title }) {
  return <section className="mt-7" data-testid={`management-section-${title.toLowerCase().replaceAll(" ", "-")}`}><div className="mb-4"><h2 className="font-display text-xl font-bold">{title}</h2><p className="mt-1 text-sm text-muted-foreground">{subtitle}</p></div>{children}</section>;
}

function KpiCard({ icon: Icon, label, onClick, testId, tone, value }) {
  const tones = { blue: "border-blue-200 bg-blue-50", green: "border-emerald-200 bg-emerald-50", amber: "border-amber-200 bg-amber-50", rose: "border-rose-200 bg-rose-50" };
  return <button className={`border p-5 text-left transition-transform hover:-translate-y-0.5 ${tones[tone]}`} data-testid={testId} onClick={onClick}><Icon className="h-5 w-5 text-primary" /><div className="mt-5 font-display text-2xl font-bold">{value ?? 0}</div><div className="mt-1 text-sm text-muted-foreground">{label}</div></button>;
}

function Fact({ label, value }) {
  return <div className="border bg-card p-4"><div className="font-display text-xl font-bold">{value ?? 0}</div><div className="mt-1 text-xs text-muted-foreground">{label}</div></div>;
}

function HealthBlock({ icon: Icon, label, metrics, onClick, testId }) {
  const fourthLabel = metrics?.waived !== undefined ? "Waived" : "Expired";
  const fourthValue = metrics?.waived !== undefined ? metrics.waived : metrics?.expired;
  return <button className="border bg-card p-5 text-left transition-transform hover:-translate-y-0.5" data-testid={testId} onClick={onClick}><div className="flex items-center gap-2 font-semibold"><Icon className="h-4 w-4 text-primary" />{label}</div><div className="mt-5 grid grid-cols-2 gap-3"><Fact label="Active" value={metrics?.active} /><Fact label="Trial" value={metrics?.trial} /><Fact label="Expiring" value={metrics?.expiring} /><Fact label={fourthLabel} value={fourthValue} /></div></button>;
}

function ChartPanel({ children, testId, title }) {
  return <section className="border bg-card p-5" data-testid={testId}><h2 className="font-display font-bold">{title}</h2><div className="mt-4">{children}</div></section>;
}

function CompactTable({ headers, rows, testId }) {
  return <div className="overflow-x-auto border bg-card" data-testid={testId}><table className="min-w-full text-sm"><thead className="bg-muted/60"><tr>{headers.map((header) => <th className="p-3 text-left" key={header}>{header}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr className="border-t" key={index}>{row.map((cell, cellIndex) => <td className="p-3" key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>;
}